import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { useCommunity } from '../contexts/CommunityContext';

function mapInviteError(err: any): string {
  const code = err?.response?.data?.error?.code;
  if (code === 'NOT_FOUND') return 'Invalid invite link.';
  if (code === 'BANNED') {
    return 'You are banned from this community. Ask an admin to send you a new invite link.';
  }
  if (code === 'INVITE_EXPIRED') {
    return 'This invitation link has expired. Ask a community admin for a new invite link.';
  }
  if (code === 'INVITE_EXHAUSTED') {
    return 'This invite link has already been used. Ask a community admin for a new one.';
  }
  if (code === 'COMMUNITY_ARCHIVED' || code === 'COMMUNITY_INACTIVE') {
    return 'This community is no longer available.';
  }
  const msg = err?.response?.data?.error?.message || err?.message || '';
  if (msg.includes('E11000') || msg.includes('duplicate key') || msg.includes('already a member')) {
    return 'You are already a member of this community.';
  }
  return msg || 'Failed to join community.';
}

export const CommunityJoinPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { redeemInviteAndJoin } = useCommunity();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prevent double-fire in React StrictMode: both renders would fire the redeem
  // request simultaneously, causing the second to see INVITE_EXHAUSTED on single-use
  // links even though the join actually succeeded.
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link.');
      setLoading(false);
      return;
    }

    // Guard: only send the request once, even if the effect re-runs (StrictMode dev, hot-reload).
    // We intentionally do NOT use a `cancelled` flag here — the cleanup sets it before the async
    // resolves in StrictMode, which would block navigation and leave the page stuck on "Joining…".
    // Since hasAttempted prevents any second request from firing, there is no double-navigation risk.
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    (async () => {
      try {
        const community = await redeemInviteAndJoin(token, { skipSelect: true });
        navigate(`/community/${community.community_id}`, { replace: true });
      } catch (err: any) {
        setError(mapInviteError(err));
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <GroupsIcon sx={{ fontSize: 48, color: '#606060', mb: 2 }} />

        {loading && !error ? (
          <>
            <CircularProgress size={28} sx={{ color: '#606060', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
              Joining community…
            </Typography>
            <Typography variant="body2" sx={{ color: '#606060' }}>
              Please wait while we add you to the group.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
              Could not join community
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
              {error}
            </Alert>
            <Button
              component={RouterLink}
              to="/chat"
              variant="contained"
              sx={{ borderRadius: 2, bgcolor: '#606060', '&:hover': { bgcolor: '#404040' } }}
            >
              Back to messages
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};
